import constants from '../../../../../../qti/Constants';
import configAutoGrow from '../plugins/autogrow/AutoGrowPlugin';
import configWordCount from '../plugins/wordcount/configWordCount';
import sessionReader from '../../../../../../store/readHelpers/SessionReader';
import UtilService from '../../../../../../services/UtilService';
import marketplacePropertiesService from '../../../../../../services/MarketplacePropertiesService';
import { t } from '../../../../../../services/i18nService';
import ckeditorService from '../../../../../../services/CKEditorService';

const clipboardItems = ['Copy', 'Paste', '-', 'Undo', 'Redo', 'IA Snapshots'];

function getParagraphIndexFromToolbarConfig(toolbar) {
    return toolbar.findIndex(configObj => configObj.name === 'paragraph');
}

if (UtilService?.isSebForMac()) {
    clipboardItems.shift();
}
function getToolbarPreset(preset) {
    let toolbar = [];
    if (preset === constants.editorToolbarModes.DEFAULT) {
        toolbar = [
            { name: 'styles', items: ['Format'] },
            { name: 'basicstyles', items: ['Bold', 'Italic', 'Underline', 'Subscript', 'Superscript', '-', 'RemoveFormat'] },
            { name: 'clipboard', items: clipboardItems },
            { name: 'paragraph', items: ['NumberedList', 'BulletedList'] },
            { name: 'insert', items: ['SpecialChar', 'Table'] },
            { name: 'diagram', items: ['Diagram'] },
            { name: 'sketches', items: ['IA Drawing'] },
            { name: 'mathEntry', items: ['ia-mathEntry-tool'] },
            { name: 'spellchecker', items: ['SpellChecker', 'Scayt'] },
            { name: 'expand', items: ['IA Expand'] },
        ];
    } else if (preset === constants.editorToolbarModes.BASIC) {
        toolbar = [
            { name: 'basicstyles', items: ['Bold', 'Italic', 'Underline'] },
            { name: 'paragraph', items: ['NumberedList', 'BulletedList'] },
            { name: 'clipboard', items: ['Undo', 'Redo'] },
        ];
    }
    if (marketplacePropertiesService.isEssayToolbarIndentEnabled()) {
        // enabling indent features based on MPP value
        const paragraphConfigIndex = getParagraphIndexFromToolbarConfig(toolbar);
        if (paragraphConfigIndex >= 0) {
            toolbar[paragraphConfigIndex].items.push(...['Outdent', 'Indent']);
        }
    }
    if (ckeditorService.shouldAddAlignmentToolbar()) {
        // enabling align features, next to paragraph actions based on MPP value
        const paragraphConfigIndex = getParagraphIndexFromToolbarConfig(toolbar);
        if (paragraphConfigIndex >= 0) {
            toolbar.splice(paragraphConfigIndex + 1, 0, { name: 'align', items: ['JustifyLeft', 'JustifyCenter', 'JustifyRight', 'JustifyBlock'] });
        }
    }
    return toolbar;
}

function resolveEditorToolbar(toolbar = constants.editorToolbarModes.DEFAULT, disabledItems = []) {
    const resolvedToolbar = typeof toolbar === 'string' ? getToolbarPreset(toolbar) : toolbar;
    return resolvedToolbar.filter((item) => {
        return disabledItems.indexOf(item.name) === -1;
    });
}

function resolveEditorSpecialChars(essaySpecialChars, defaultSpecialChars) {
    return essaySpecialChars || defaultSpecialChars;
}

function resolveEditorExtraPlugins(disabledItems = []) {
    const extraPlugins = [
        'ia-word-count',
        'wsc',
        'fixed',
        'justify',
        'ia-expand-tool',
        'ia-mathEntry-tool',
        'ia-snapshots-tool',
        'a11yhelp',
    ];
    if (marketplacePropertiesService.isEnableCandidateOldDrawingOption()) {
        extraPlugins.push('ia-drawing-tool');
    }

    if (ckeditorService.shouldUseIndentPlugin()) {
        extraPlugins.push('indentblock');
        extraPlugins.push('indent');
    }
    return extraPlugins.filter((item) => {
        return disabledItems.indexOf(item) === -1;
    }).join(',');
}

export default function getEditorConfig(
    CKEDITOR,
    {
        autoExpand,
        disabledMenuItems,
        editorHeight,
        isReadOnly,
        isViewMediaPrint,
        language,
        maxWords,
        showWordCount,
        editorToolbar,
        editorSpecialChars,
    }
) {
    const defaultSpecialChars = CKEDITOR.config.specialChars.concat([['&omega;', 'Omega'], ['&Omega;', 'Omega']]);
    const specialChars = resolveEditorSpecialChars(editorSpecialChars, defaultSpecialChars);
    const toolbar = resolveEditorToolbar(editorToolbar, disabledMenuItems);
    const extraPlugins = resolveEditorExtraPlugins(disabledMenuItems);

    let entitiesConfig;

    if (marketplacePropertiesService.isCkeditorProcessNumericalEnabled()) {
        entitiesConfig = {
            entities_processNumerical: 'force',
        };
    } else {
        entitiesConfig = {
            entities: false,
            entities_greek: false,
            entities_latin: false,
        };
    }

    return {
        customConfig: '',
        // Disabling resize cursors (for table/image) and add/remove columns/rows cursors (for table) (FF)
        disableObjectResizing: true,
        disableNativeTableHandles: true,
        extraPlugins,
        extraAllowedContent: {
            img: {
                attributes: 'src,alt,width,height,data-snapshot',
                classes: 'ia-drawing',
            },
            span: {
                attributes: 'data-mathentryvalue',
                classes: 'math-tex',
            },
            pre: {
                classes: 'pre-formatted',
            },
        },
        format_pre: { element: 'pre', attributes: { class: 'pre-formatted' } },
        language,
        mathJaxLib: sessionReader.isOfflineMode()
            ? `${constants.staticLibPathsOffline.MATHJAX2}MathJax.js?config=TeX-AMS_HTML`
            : `${constants.staticLibPaths.MATHJAX2}MathJax.js?config=TeX-AMS_HTML`,
        readOnly: isReadOnly,
        removePlugins: 'resize,wordcount,scayt,wsc',
        specialChars,
        // setting timestamp to empty string to cache it and preload
        timestamp: '',
        toolbar,
        uiColor: '#f7f7f7',
        title: t('interactions.extendedTextInteraction.ckEditorWrapperAriaLabel'),
        undoStackSize: 100,
        wordcount: configWordCount(maxWords, showWordCount),

        ...configAutoGrow(autoExpand, editorHeight, isViewMediaPrint),
        ...entitiesConfig,
    };
}
